import React, { useCallback, useMemo } from 'react';
import { useAccount, useChainId, useSwitchChain } from 'wagmi';

import { useMetamaskAuth } from 'hooks/utility';
import { useTypedSelector } from 'store';
import { Modal } from 'ui/Modal/Modal';
import { web3Configs } from 'web3';

import { Button } from '../Button/Button';

import { Metamask } from 'assets/images/wallets/metamask';

interface IChangePairNetworkProps {
  onClose: () => void;
}

const ChangePairNetwork: React.FC<IChangePairNetworkProps> = ({ onClose }) => {
  const { address } = useAccount();
  const chainId = useChainId();
  const { switchChainAsync } = useSwitchChain();

  const { connect } = useMetamaskAuth();
  const dexPair = useTypedSelector(store => store.pairs.selectedDexPair)!;

  const changeNetwork = useCallback(
    async ({
      chainId,
      chainName,
      rpcUrls,
      nativeCurrency,
    }: {
      chainId: number;
      chainName: string;
      rpcUrls: string[];
      nativeCurrency: {
        name: string;
        symbol: string;
        decimals: number;
      };
    }) => {
      try {
        await switchChainAsync({
          chainId,
          addEthereumChainParameter: {
            chainName,
            rpcUrls,
            nativeCurrency,
          },
        });
      } catch (error) {
        console.log(error);
      }
    },
    [switchChainAsync],
  );

  const handleChangeNetwork = useCallback(() => {
    onClose();

    if (!address) {
      return connect();
    }

    for (const network of Object.keys(web3Configs)) {
      if (dexPair.network === network && (!chainId || chainId !== web3Configs[network].chainId)) {
        const { chainName, chainId, nativeCurrency, rpcUrls } = web3Configs[network];

        return changeNetwork({ chainName, chainId, nativeCurrency, rpcUrls });
      }
    }
  }, [address, dexPair, connect, changeNetwork, onClose, chainId]);

  const buttonText = useMemo(() => {
    if (!address) {
      return 'To continue please connect metamask';
    }

    for (const network of Object.keys(web3Configs)) {
      if (dexPair.network === network && chainId !== web3Configs[network].chainId) {
        return `To continue please switch network to ${web3Configs[network].chainName}`;
      }
    }

    return null;
  }, [address, dexPair, chainId]);

  return (
    <Modal onClose={onClose} title="Invalid metamask connection">
      {buttonText && (
        <Button color="secondary" size="small" onClick={handleChangeNetwork}>
          <Metamask />
          <span>{buttonText}</span>
        </Button>
      )}
    </Modal>
  );
};

export { ChangePairNetwork };
